/**
 * Sidebar v2 controller: assembles the section model, search, shelf state,
 * and the row actions and drag machine on top of the client-side overlay.
 *
 * Ported from pingdotgg/t3code `apps/web/src/components/Sidebar.tsx`
 * (default-export component body, ~lines 2144-3800) at pin
 * e3e7cc3fc2e02966efe4efc45216dadcd046bfbb (MIT; see
 * THIRD-PARTY-NOTICES.md). Adapted: pin, order keys, and snooze write to
 * forge's uiState overlay instead of upstream's server-side thread
 * commands (settle stays on forge's server path); the project scope is
 * session state; per-environment capability gates, draft rows, file-drop
 * plumbing have no forge equivalent yet
 * (documented seams). Actions and drag live in their own modules.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Schema } from "effect";
import { useNavigate } from "@tanstack/react-router";
import { scopedThreadKey, scopeThreadRef } from "@cmmd-forge/client-runtime";
import type { ScopedThreadRef, TimestampFormat } from "@cmmd-forge/contracts";

import { useThreadActions } from "~/hooks/useThreadActions";
import { useUiStateStore } from "~/uiStateStore";
import { useThreadSelectionStore } from "~/threadSelectionStore";
import { useSidebar } from "~/components/ui/sidebar";
import { useLocalStorage } from "~/hooks/useLocalStorage";
import type { SidebarThreadSummary } from "~/types";

import { useSidebarV2DragController, type SidebarV2DragResult } from "./sidebarV2ControllerDrag";
import { useSidebarV2RowActions } from "./sidebarV2ControllerActions";
import {
  applySidebarOverlay,
  partitionSidebarV2Threads,
  sidebarThreadKey,
  type SidebarV2Thread,
} from "./sidebarV2Threads";
import { searchSidebarThreads, threadBranchSearchTerms } from "./sidebarV2Search";
import { useSidebarV2ContentSearch } from "./useSidebarV2ContentSearch";
import { useSnoozeWakeTimer } from "./sidebarV2Snooze";
import { resolveAdjacentThreadId } from "./sidebarV2List";
import { SETTLED_TAIL_INITIAL_COUNT, SETTLED_TAIL_PAGE_COUNT } from "./sidebarV2Chrome";

export interface SidebarV2ProjectScopeEntry {
  readonly projectKey: string;
  readonly displayName: string;
  readonly environmentId: string;
  readonly projectId: string;
  readonly cwd: string | null;
  /** Member project scoped keys ("env:projectId") of this group. */
  readonly memberKeys: readonly string[];
}

/** Combine persisted overlays, shelf paging, search, selection, and row actions for the v2 sidebar. */
// fallow-ignore-next-line complexity -- v2 orchestration hook composing focused sub-hooks (see sidebarV2* modules)
export function useSidebarV2Controller(input: {
  readonly allSidebarThreads: readonly SidebarThreadSummary[];
  readonly projectEntries: readonly SidebarV2ProjectScopeEntry[];
  readonly isProjectInventoryLoading?: boolean;
  readonly timestampFormat: TimestampFormat;
  readonly terminalOpen: boolean;
  readonly routeThreadRef: ScopedThreadRef | null;
}) {
  const navigate = useNavigate();
  const { isMobile, setOpenMobile } = useSidebar();
  const pinnedThreadById = useUiStateStore((s) => s.pinnedThreadById);
  const pinOrderKeyById = useUiStateStore((s) => s.pinOrderKeyById);
  const activeOrderKeyById = useUiStateStore((s) => s.activeOrderKeyById);
  const threadSnoozeById = useUiStateStore((s) => s.threadSnoozeById);
  const markThreadVisited = useUiStateStore((s) => s.markThreadVisited);
  const setThreadPinned = useUiStateStore((s) => s.setThreadPinned);
  const setThreadPinOrderKey = useUiStateStore((s) => s.setThreadPinOrderKey);
  const setThreadActiveOrderKey = useUiStateStore((s) => s.setThreadActiveOrderKey);
  const setThreadSnooze = useUiStateStore((s) => s.setThreadSnooze);
  const clearSelection = useThreadSelectionStore((s) => s.clearSelection);
  const { settleThread, unsettleThread, archiveThread, deleteThread } = useThreadActions();

  const [projectScopeKey, persistProjectScopeKey] = useLocalStorage(
    "forge:sidebar-v2-project-scope",
    null as string | null,
    Schema.NullOr(Schema.String),
  );
  const setProjectScopeKey = useCallback(
    (key: string | null) => {
      clearSelection();
      persistProjectScopeKey(key);
    },
    [clearSelection, persistProjectScopeKey],
  );
  const scopedProjectKeys = useMemo(() => {
    if (projectScopeKey === null) return null;
    const entry = input.projectEntries.find((project) => project.projectKey === projectScopeKey);
    return entry ? (new Set(entry.memberKeys) as ReadonlySet<string>) : null;
  }, [input.projectEntries, projectScopeKey]);
  useEffect(() => {
    if (
      projectScopeKey !== null &&
      !input.isProjectInventoryLoading &&
      input.projectEntries.length > 0 &&
      !input.projectEntries.some((project) => project.projectKey === projectScopeKey)
    ) {
      setProjectScopeKey(null);
    }
  }, [input.isProjectInventoryLoading, input.projectEntries, projectScopeKey, setProjectScopeKey]);

  const overlay = useMemo(
    () => ({ pinnedThreadById, pinOrderKeyById, activeOrderKeyById, threadSnoozeById }),
    [activeOrderKeyById, pinOrderKeyById, pinnedThreadById, threadSnoozeById],
  );
  const overlaidThreads = useMemo(
    () =>
      input.allSidebarThreads.map((thread) =>
        applySidebarOverlay(thread, sidebarThreadKey(thread), overlay),
      ),
    [input.allSidebarThreads, overlay],
  );

  // Snooze wake times are second-precise; a tick re-runs the partition at
  // the next wake boundary so a woken thread leaves the shelf on time.
  const [snoozeWakeTick, bumpSnoozeWakeTick] = useState(0);
  const partition = useMemo(
    () =>
      partitionSidebarV2Threads({
        threads: overlaidThreads,
        scopedProjectKeys,
        optimisticDrop: null,
        snoozeWakeTick,
      }),
    [overlaidThreads, scopedProjectKeys, snoozeWakeTick],
  );
  const { pinnedThreads, activeThreads, snoozedThreads, settledThreads } = partition;

  useSnoozeWakeTimer(snoozedThreads[0]?.snoozedUntil ?? null, snoozeWakeTick, bumpSnoozeWakeTick);

  // Settled tail paging + shelf expansion. Fresh keys start collapsed: the
  // shelves are history, out of the way but never gone.
  const [settledVisibleCount, setSettledVisibleCount] = useState(SETTLED_TAIL_INITIAL_COUNT);
  const [settledShelfExpanded, setSettledShelfExpanded] = useLocalStorage(
    "forge:sidebar-v2-settled-expanded",
    false,
    Schema.Boolean,
  );
  const [snoozedShelfExpanded, setSnoozedShelfExpanded] = useLocalStorage(
    "forge:sidebar-v2-snoozed-expanded",
    false,
    Schema.Boolean,
  );
  const visibleSettledThreads = useMemo(
    () => settledThreads.slice(0, settledVisibleCount),
    [settledThreads, settledVisibleCount],
  );
  const hiddenSettledCount = settledThreads.length - visibleSettledThreads.length;

  // Keep local title and branch matches immediate while server content search
  // waits for the debounce and the connected environments to respond.
  const [threadSearchQuery, setThreadSearchQuery] = useState("");
  const [activeSearchResultIndex, setActiveSearchResultIndex] = useState(0);
  const isSearchingThreads = threadSearchQuery.trim().length > 0;
  const searchableThreads = useMemo(
    () => [...pinnedThreads, ...activeThreads, ...snoozedThreads, ...settledThreads],
    [activeThreads, pinnedThreads, settledThreads, snoozedThreads],
  );
  const searchEnvironmentIds = useMemo(
    () => [...new Set(searchableThreads.map((thread) => thread.environmentId))],
    [searchableThreads],
  );
  const contentSearch = useSidebarV2ContentSearch(searchEnvironmentIds, threadSearchQuery);
  const threadSearchResults = useMemo(
    () =>
      searchSidebarThreads(
        searchableThreads,
        threadSearchQuery,
        new Set(contentSearch.matches.keys()),
        threadBranchSearchTerms,
      ),
    [contentSearch.matches, searchableThreads, threadSearchQuery],
  );

  const orderedThreadKeys = useMemo(
    () => [...pinnedThreads, ...activeThreads].map((thread) => sidebarThreadKey(thread)),
    [activeThreads, pinnedThreads],
  );
  const orderedThreadKeysRef = useRef(orderedThreadKeys);
  orderedThreadKeysRef.current = orderedThreadKeys;
  const threadByKey = useMemo<Map<string, SidebarV2Thread>>(
    () => new Map(searchableThreads.map((thread) => [sidebarThreadKey(thread), thread] as const)),
    [searchableThreads],
  );

  const navigateToThread = useCallback(
    (threadRef: ScopedThreadRef) => {
      markThreadVisited(scopedThreadKey(threadRef), new Date().toISOString());
      if (isMobile) setOpenMobile(false);
      void navigate({
        to: "/$environmentId/$threadId",
        params: { environmentId: threadRef.environmentId, threadId: threadRef.threadId },
      });
    },
    [isMobile, markThreadVisited, navigate, setOpenMobile],
  );
  const selectThreadSearchResult = useCallback(
    (thread: SidebarThreadSummary) => {
      setThreadSearchQuery("");
      setActiveSearchResultIndex(0);
      navigateToThread(scopeThreadRef(thread.environmentId, thread.id));
    },
    [navigateToThread],
  );

  // Forge fan-out: workers grouped under their master thread.
  const workerRowsByThreadKey = useMemo(() => {
    const rows = new Map<string, SidebarThreadSummary[]>();
    for (const thread of input.allSidebarThreads) {
      if (thread.kind !== "worker" || thread.parentThreadId === null) continue;
      const parentKey = sidebarThreadKey({
        environmentId: thread.environmentId,
        id: thread.parentThreadId,
      });
      const list = rows.get(parentKey) ?? [];
      list.push(thread);
      rows.set(parentKey, list);
    }
    return rows;
  }, [input.allSidebarThreads]);

  const threadByKeyRef = useRef(threadByKey);
  threadByKeyRef.current = threadByKey;
  const actions = useSidebarV2RowActions({
    timestampFormat: input.timestampFormat,
    pinOrderKeyById,
    markThreadVisited,
    markThreadUnread: useUiStateStore.getState().markThreadUnread,
    setThreadPinned,
    setThreadPinOrderKey,
    setThreadActiveOrderKey,
    setThreadSnooze,
    settleThread: settleThread as unknown as (target: ScopedThreadRef) => unknown,
    unsettleThread: unsettleThread as unknown as (target: ScopedThreadRef) => unknown,
    archiveThread: archiveThread as unknown as (
      target: ScopedThreadRef,
      opts: Record<string, never>,
    ) => unknown,
    deleteThread: deleteThread as unknown as (
      target: ScopedThreadRef,
      opts: Record<string, never>,
    ) => unknown,
    navigateToThread,
    orderedThreadKeysRef,
    threadByKeyRef,
  });

  const drag: SidebarV2DragResult = useSidebarV2DragController({
    overlaidThreads,
    pinnedThreads,
    activeThreads,
    snoozedThreads,
    settledThreads,
    visibleSettledThreads,
    snoozedExpanded: snoozedShelfExpanded,
    settledExpanded: settledShelfExpanded,
    renamingThreadKey: actions.renamingThreadKey,
    attemptSettle: actions.attemptSettle,
    attemptUnsettle: actions.attemptUnsettle,
    pinThread: actions.pinThread,
    unpinThread: actions.unpinThread,
    setThreadPinned,
    setThreadSnooze,
    setThreadPinOrderKey,
    setThreadActiveOrderKey,
    threadByKey,
  });

  const resolveSidebarShortcutJump = useCallback(
    (direction: "previous" | "next") => {
      const currentKey =
        input.routeThreadRef === null ? null : scopedThreadKey(input.routeThreadRef);
      return resolveAdjacentThreadId({
        threadIds: orderedThreadKeys,
        currentThreadId: currentKey,
        direction,
      });
    },
    [input.routeThreadRef, orderedThreadKeys],
  );

  return {
    projectScopeKey,
    setProjectScopeKey,
    partition,
    snoozeNow: partition.snoozeNow,
    timestampFormat: input.timestampFormat,
    workerRows: (threadKey: string) =>
      (workerRowsByThreadKey.get(threadKey) ?? []).map((worker) => ({
        id: worker.id,
        environmentId: worker.environmentId,
        title: worker.title,
        status: String(worker.status),
        workerRole: worker.workerRole,
      })),
    settledShelfExpanded,
    toggleSettledShelf: () => setSettledShelfExpanded((value) => !value),
    snoozedShelfExpanded,
    toggleSnoozedShelf: () => setSnoozedShelfExpanded((value) => !value),
    visibleSettledThreads,
    hiddenSettledCount,
    showMoreSettled: () => setSettledVisibleCount((count) => count + SETTLED_TAIL_PAGE_COUNT),
    threadSearchQuery,
    setThreadSearchQuery,
    activeSearchResultIndex,
    setActiveSearchResultIndex,
    isSearchingThreads,
    threadSearchResults,
    contentSearchMatches: contentSearch.matches,
    isContentSearchPending: contentSearch.isPending,
    orderedThreadKeys,
    threadByKey,
    workerRowsByThreadKey,
    clearThreadSearch: () => {
      setThreadSearchQuery("");
      setActiveSearchResultIndex(0);
    },
    selectThreadSearchResult: selectThreadSearchResult as unknown as (
      thread: SidebarThreadSummary,
    ) => void,
    navigateToThread,
    handleThreadClick: actions.handleThreadClick,
    acknowledgeWoke: actions.acknowledgeWoke,
    openPullRequestExternal: actions.openPullRequestExternal,
    attemptSettle: actions.attemptSettle,
    attemptUnsettle: actions.attemptUnsettle,
    snoozeThread: actions.snoozeThread,
    unsnoozeThread: actions.unsnoozeThread,
    pinThread: actions.pinThread,
    unpinThread: actions.unpinThread,
    renamingThreadKey: actions.renamingThreadKey,
    renamingTitle: actions.renamingTitle,
    setRenamingTitle: actions.setRenamingTitle,
    startThreadRename: actions.startThreadRename,
    cancelThreadRename: actions.cancelThreadRename,
    commitRename: actions.commitRename,
    navigateToWorker: (worker: {
      readonly environmentId: SidebarThreadSummary["environmentId"];
      readonly id: SidebarThreadSummary["id"];
    }) => {
      navigateToThread(scopeThreadRef(worker.environmentId, worker.id));
    },
    attemptArchive: actions.attemptArchive,
    attemptDelete: actions.attemptDelete,
    handleThreadContextMenu: actions.handleThreadContextMenu,
    sidebarListItems: drag.sidebarListItems,
    dragState: drag.dragState,
    handleThreadDragStart: drag.handleThreadDragStart,
    handleThreadDragOver: drag.handleThreadDragOver,
    handleThreadDragEnd: drag.handleThreadDragEnd,
    resolveSidebarShortcutJump,
    clearSelection,
  };
}
